import _ from 'lodash';
import { Observable } from 'rxjs';
import { startSubmit, stopSubmit, initialize } from 'redux-form/immutable';
import type { ActionsObservable } from 'redux-observable';
import { Store } from 'redux';

import addressToLocation from 'utils/addressToLocation';
import OrganizationApi from 'entities/organization/api';
import CauseApi from 'entities/cause/api';
import SkillsApi from 'entities/skill/api';
import { selectOrganization, selectUser } from 'entities/user/selectors';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import { selectCauses } from 'entities/cause/selectors';
import { selectSkills } from 'entities/skill/selectors';
import { STORE as STORE_USER } from 'entities/user/constants';
import type { State } from 'reducers';
import { validateLocationObject } from 'utils/validateLocationObject';
import LocationObject from 'entities/locationObject/model';

import {
  initSuccessAction,
  saveSuccessAction,
  saveFailedAction,
  initFailedAction,
  requestAddressChangeAction,
} from './actions';
import { INIT, SAVE } from './constants';
import { FORM_IDENTIFIER as PRESENCE_FORM_IDENTIFIER } from './PresenceFormSection';

const init = (action$: ActionsObservable<any>, store: Store<State>) =>
  action$.ofType(INIT, STORE_USER).mergeMap(() => {
    const state = store.getState();
    const actions = [];

    const causes = selectCauses(state);
    if (causes.size === 0) {
      actions.push(CauseApi.fetchAll());
    }

    const skills = selectSkills(state);
    if (skills.size === 0) {
      actions.push(SkillsApi.fetchAll());
    }

    if (actions.length === 0) {
      return Observable.of(initSuccessAction());
    }

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) => Observable.of(initFailedAction(e)));
  });

const save = (action$: ActionsObservable<any>, store: Store<State>) =>
  action$.ofType(SAVE).mergeMap((payload) => {
    const { FORM_IDENTIFIER } = payload;
    const user = selectUser(store.getState());
    const isPresenceForm = FORM_IDENTIFIER === PRESENCE_FORM_IDENTIFIER;

    return Observable.concat(
      Observable.of(startSubmit(FORM_IDENTIFIER)),
      Observable.if(
        () =>
          (!user?.isAdmin || (user?.isAdmin && payload.data.location)) &&
          !payload.data.manualEntry &&
          isPresenceForm,
        Observable.defer(() => addressToLocation(payload.data.location)),
        Observable.of({
          location: undefined,
          locationObject: {},
        })
      )
        .mergeMap(({ location, locationObject }) => {
          const data = { ...payload.data };
          const { id } = data;
          if (isPresenceForm) {
            if (payload.data.manualEntry) {
              data.locationObject = payload.data.locationObject;
              data.location = payload.data.locationObject?.geo;
            } else {
              try {
                validateLocationObject(new LocationObject(locationObject));
              } catch (err: any) {
                return Observable.of(
                  saveFailedAction(err.message, 'saveFailed'),
                  // @ts-expect-error stopSubmit type does not match Observable types
                  stopSubmit(FORM_IDENTIFIER),
                  showErrorAction(
                    err.message || 'An error occurred during saving, please try again.'
                  )
                );
              }
              data.locationObject = locationObject;
              data.location = location;
            }

            // @NOTE-DP: we shard nonprofit db based on countries/states, so when the nonprofit tries to jump across this boundary, we shouldn't let them do it directly
            if (
              user?.organization?.isNonprofit() &&
              (locationObject.countryCode !== user?.organization.locationObject.countryCode ||
                (locationObject.countryCode === 'US' &&
                  locationObject.stateCode !== user?.organization.locationObject.stateCode))
            ) {
              return Observable.of(
                requestAddressChangeAction(data.locationObject),
                // @ts-expect-error stopSubmit type does not match Observable types
                stopSubmit(FORM_IDENTIFIER)
              );
            }
          }

          if (data.verificationStatus === '-') {
            data.verificationStatus = undefined;
          }

          delete data.manualEntry;
          delete data.NCESId;
          delete data.id;
          delete data.createdAt;
          delete data.modifiedAt;
          delete data.locations;
          delete data.rating;
          delete data.reviews;
          delete data.pictures;
          delete data.type;

          return OrganizationApi.update(id, data)
            .mergeMap((action) => {
              const state = store.getState();
              const populatedOrganization = selectOrganization(state);

              let updatedOrganization = populatedOrganization;

              // @NOTE-DP: only set the keys which we were actually updating
              Object.keys(data).forEach((key) => {
                updatedOrganization = updatedOrganization?.set(
                  key, // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  action.organization.get(key)
                );
              });

              return [
                { ...action, organization: updatedOrganization },
                saveSuccessAction(),
                stopSubmit(FORM_IDENTIFIER),
                initialize(FORM_IDENTIFIER, updatedOrganization?.toJS()),
                showMessageAction('Changes Saved!'),
              ];
            })
            .catch((err) =>
              Observable.of(
                saveFailedAction(err, 'saveFailed'),
                // @ts-expect-error stopSubmit type does not match Observable types
                stopSubmit(FORM_IDENTIFIER),
                showErrorAction(
                  err.responseBody?.message || 'An error occurred during saving, please try again.'
                )
              )
            );
        })
        .catch((e) =>
          Observable.of(
            saveFailedAction(e, 'addressInvalid', {
              address: payload.data.location,
            }),
            // @ts-expect-error stopSubmit type does not match Observable types
            stopSubmit(FORM_IDENTIFIER),
            showErrorAction('Address could not be found.')
          )
        )
    );
  });

export default [init, save];
